import React, { useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import {
	Popover,
	PopoverTrigger,
	PopoverContent,
} from '@/components/ui/popover';
import {
	Command,
	CommandInput,
	CommandList,
	CommandEmpty,
	CommandGroup,
	CommandItem,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
	BottomSheet,
	BottomSheetTextButton,
	MobileSearchField,
} from '@/components/mobile';
import { Check, ChevronsUpDown, Users } from 'lucide-react';

import { User } from '@/types/index';
import {
	CalendarDepartment,
	useLocationDepartments,
} from '@/components/event-calendar/utils/departments';
import { useUserDisplayNames } from '@/hooks/useUserColorOptions';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import getAvatarColor from '@/utils/getAvatarColor';
import getInitials from '@/utils/getInitials';
import { getUserDisplayName } from '@/utils/userColorGenerator';
import * as PopoverPrimitive from '@radix-ui/react-popover';

export const MultiUserSelector = ({
	onSelectUser,
	_selectedUsers = [],
	title = 'Select employee',
	sheetTitle,
	max,
	onCloseMenu,
	disabled,
	className,
	placeholderClassName = 'text-sm mx-1',
	side = 'bottom',
	onSelectDepartment,
	_selectedDepartment,
}: {
	onSelectUser: (user: User) => void;
	/** When set, the location's departments are listed above people */
	onSelectDepartment?: (department: CalendarDepartment) => void;
	/** The department this item is linked to, shown first in the label */
	_selectedDepartment?: string | null;
	_selectedUsers: string[] | undefined;
	onCloseMenu?: () => void;
	title?: string;
	/** The phone sheet's title (`title` is often a placeholder like "Unassigned") */
	sheetTitle?: string;
	/** Most people that can be picked; the rest are disabled once it's reached */
	max?: number;
	disabled?: boolean;
	className?: string;
	placeholderClassName?: string;
	side?: 'top' | 'bottom' | 'left' | 'right';
}) => {
	const allUsers = useSelector((state: any) =>
		state.users.filter((u: any) => u.active),
	);
	const displayNames = useUserDisplayNames();
	const getUserLabel = (user: User) =>
		displayNames.get(user._id) || user.name || user.username;
	const departments = useLocationDepartments();
	const selectedDepartment = departments.find(
		(department) => department._id === _selectedDepartment,
	);
	const selectedLabels = [
		selectedDepartment?.name,
		...(_selectedUsers || []).map((selectedUser) => {
			const user = allUsers.find((user: User) => user._id === selectedUser);
			return user ? getUserLabel(user) : '';
		}),
	].filter(Boolean) as string[];
	const label = selectedLabels.join(', ');
	const atLimit = max !== undefined && (_selectedUsers?.length ?? 0) >= max;
	const isPicked = (user: User) =>
		_selectedUsers.some((_selectedUser) => _selectedUser === user._id);

	// Phones get a bottom sheet instead of the popover. The open state and the
	// search live up here so rotating across 768px keeps both.
	const isMobile = useIsMobile();
	const [openUserFilter, setOpenUserFilter] = useState(false);
	const [search, setSearch] = useState('');
	const triggerRef = useRef<HTMLButtonElement>(null);

	// Rotated from the sheet to the popover: the form around the trigger
	// re-lays out for the new width (its scroll area can change too, a render
	// or two later), so once it has settled bring the trigger back into view
	// for the popover to hang from. Only after that switch, so opening the
	// popover never scrolls the page.
	const wasMobile = useRef(isMobile);
	useEffect(() => {
		const rotatedOpen = wasMobile.current && !isMobile && openUserFilter;
		wasMobile.current = isMobile;
		if (!rotatedOpen) return;
		const timer = window.setTimeout(() => {
			const trigger = triggerRef.current;
			const rect = trigger?.getBoundingClientRect();
			if (rect && (rect.top < 0 || rect.bottom > window.innerHeight))
				trigger?.scrollIntoView({ block: 'center' });
		}, 300);
		return () => window.clearTimeout(timer);
	}, [isMobile, openUserFilter]);

	const handleCloseMenu = (open: boolean) => {
		setOpenUserFilter(open);

		if (!open) {
			setSearch('');
			onCloseMenu?.();
		}
	};

	return (
		<>
			<Popover
				open={openUserFilter && !isMobile}
				onOpenChange={handleCloseMenu}
				modal
			>
				<PopoverTrigger asChild>
					<Button
						ref={triggerRef}
						variant="outline"
						// A plain button on phones: it opens a sheet, not a listbox (and
						// the popover content its aria-controls would name never renders)
						role={isMobile ? undefined : 'combobox'}
						{...(isMobile ? { 'aria-controls': undefined } : {})}
						aria-expanded={openUserFilter}
						className={`justify-between text-wrap h-auto ${className}`}
						disabled={disabled}
					>
						{label ? (
							<>
								{/* Wide screens: the whole list, as it always was */}
								<span className="truncate max-xl:hidden">{label}</span>
								<PickedSummary labels={selectedLabels} />
							</>
						) : (
							<span className="truncate">
								<span
									className={`${placeholderClassName} ${
										disabled ? 'text-muted-foreground' : ''
									}`}
								>
									{title}
								</span>
							</span>
						)}
						<ChevronsUpDown className="opacity-50" size={12} />
					</Button>
				</PopoverTrigger>
				{!isMobile && (
					<PopoverPrimitive.Portal>
						{/* Short screens (landscape phones): the search stays put and the list scrolls under it */}
						<PopoverContent
							className="p-0 rounded-lg max-xl:flex max-xl:flex-col"
							side={side}
						>
							<Command className="rounded-lg max-xl:min-h-0">
								<CommandInput
									placeholder={title}
									value={search}
									onValueChange={setSearch}
									className="touch:h-11"
								/>
								<CommandList className="max-xl:min-h-0">
									<CommandEmpty>No user found.</CommandEmpty>
									{atLimit && (
										<p className="px-3 pt-2 text-xs text-muted-foreground">
											You can choose up to {max} people.
										</p>
									)}
									{onSelectDepartment && departments.length > 0 && (
										<CommandGroup heading="Departments">
											{departments.map((department) => (
												<CommandItem
													key={department._id}
													value={`department ${department.name} ${department._id}`}
													onSelect={() => onSelectDepartment(department)}
													className="z-10 clickable-item touch:min-h-11"
												>
													<Users className="h-4 w-4 text-muted-foreground" />
													{department.name || 'Unnamed department'}
													<Check
														className={`ml-auto ${
															department._id === _selectedDepartment
																? 'opacity-100'
																: 'opacity-0'
														}`}
													/>
												</CommandItem>
											))}
										</CommandGroup>
									)}
									<CommandGroup heading={onSelectDepartment ? 'People' : undefined}>
										{allUsers.map((user: User) => (
											<CommandItem
												key={user._id}
												value={`${getUserLabel(user)} ${user.username || ''} ${user._id}`}
												onSelect={() => onSelectUser(user)}
												disabled={atLimit && !isPicked(user)}
												className="z-10 clickable-item touch:min-h-11"
											>
												{getUserLabel(user)}
												<Check
													className={`ml-auto ${
														isPicked(user) ? 'opacity-100' : 'opacity-0'
													}`}
												/>
											</CommandItem>
										))}
									</CommandGroup>
								</CommandList>
							</Command>
						</PopoverContent>
					</PopoverPrimitive.Portal>
				)}
			</Popover>
			{isMobile && (
				<PeopleSheet
					open={openUserFilter}
					onOpenChange={handleCloseMenu}
					// The sheet may have opened from the popover after a rotation, with
					// nothing left to hand focus back to: use the trigger.
					onCloseAutoFocus={(event) => {
						const active = document.activeElement;
						if (active && active !== document.body) return;
						event.preventDefault();
						triggerRef.current?.focus({ preventScroll: true });
					}}
					title={sheetTitle || title}
					search={search}
					onSearchChange={setSearch}
					users={allUsers}
					getUserLabel={getUserLabel}
					isPicked={isPicked}
					onSelectUser={onSelectUser}
					pickedCount={_selectedUsers?.length ?? 0}
					max={max}
					atLimit={atLimit}
					departments={onSelectDepartment ? departments : []}
					selectedDepartment={_selectedDepartment}
					onSelectDepartment={onSelectDepartment}
				/>
			)}
		</>
	);
};

/**
 * Below 1280px the trigger reads "Ana Smith, Ben Jones +3". Each of the two
 * names truncates in its own grid column (a short one keeps its full width, two
 * long ones share the space), so both people and the count stay visible. The
 * columns have no minimum width, so a long list can't widen the form around
 * the trigger. VoiceOver reads the full list.
 */
const PickedSummary = ({ labels }: { labels: string[] }) => {
	const shown = labels.slice(0, 2);
	const more = labels.length - shown.length;
	const columns = [
		'minmax(0,max-content)',
		...(shown.length > 1 ? ['auto', 'minmax(0,max-content)'] : []),
		...(more > 0 ? ['auto'] : []),
	];
	return (
		<span
			className="grid min-w-0 items-center xl:hidden"
			style={{ gridTemplateColumns: columns.join(' ') }}
		>
			<span aria-hidden className="truncate">
				{shown[0]}
			</span>
			{shown.length > 1 && (
				<>
					<span aria-hidden>{',\u00a0'}</span>
					<span aria-hidden className="truncate">
						{shown[1]}
					</span>
				</>
			)}
			{more > 0 && (
				<span aria-hidden className="pl-1.5 text-muted-foreground">
					+{more}
				</span>
			)}
			<span className="sr-only">{labels.join(', ')}</span>
		</span>
	);
};

// The store's users carry an email, though the User type leaves it out
const emailOf = (user: User) => (user as User & { email?: string }).email;

const SECTION_TITLE =
	'px-1 pb-1 pt-3 text-[13px] uppercase tracking-wide text-muted-foreground';

/**
 * The phone picker: a bottom sheet with a search field and 44px checkable
 * rows. It stays open while people are picked; Done (or a drag down) closes it
 * and hands focus back to the trigger.
 */
const PeopleSheet = ({
	open,
	onOpenChange,
	onCloseAutoFocus,
	title,
	search,
	onSearchChange,
	users,
	getUserLabel,
	isPicked,
	onSelectUser,
	pickedCount,
	max,
	atLimit,
	departments,
	selectedDepartment,
	onSelectDepartment,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCloseAutoFocus: (event: Event) => void;
	title: string;
	search: string;
	onSearchChange: (search: string) => void;
	users: User[];
	getUserLabel: (user: User) => string | undefined;
	isPicked: (user: User) => boolean;
	onSelectUser: (user: User) => void;
	pickedCount: number;
	max?: number;
	atLimit: boolean;
	departments: CalendarDepartment[];
	selectedDepartment?: string | null;
	onSelectDepartment?: (department: CalendarDepartment) => void;
}) => {
	const query = search.trim().toLowerCase();
	const matches = (...texts: (string | undefined)[]) =>
		!query || texts.some((text) => text?.toLowerCase().includes(query));
	const people = users.filter((user) =>
		matches(getUserLabel(user), user.username, emailOf(user)),
	);
	const shownDepartments = departments.filter((department) =>
		matches(department.name || 'Unnamed department'),
	);
	// The limit is on people; without one, a picked department counts too
	const checkedCount =
		pickedCount +
		(departments.some((department) => department._id === selectedDepartment) ? 1 : 0);
	const description =
		max !== undefined
			? pickedCount
				? `${pickedCount} selected · up to ${max}`
				: `Choose up to ${max} people`
			: checkedCount
				? `${checkedCount} selected`
				: undefined;

	return (
		<BottomSheet
			open={open}
			onOpenChange={onOpenChange}
			onCloseAutoFocus={onCloseAutoFocus}
			title={title}
			description={description}
			// Most of the screen once there's a list to search through
			size={users.length + departments.length > 6 ? 'large' : 'auto'}
			headerRight={
				<BottomSheetTextButton strong onClick={() => onOpenChange(false)}>
					Done
				</BottomSheetTextButton>
			}
		>
			{/* Pinned over the list (the body's top padding counts for sticky, hence -top-2) */}
			<div className="sticky -top-2 z-10 -mx-4 -mt-2 bg-background px-4 pb-2 pt-2 dark:bg-zinc-900">
				<MobileSearchField
					value={search}
					onChange={onSearchChange}
					placeholder={departments.length ? 'Search people and departments' : 'Search people'}
				/>
				<p
					role="status"
					className={atLimit ? 'mt-2 rounded-lg bg-muted px-3 py-2 text-[13px] text-muted-foreground' : 'sr-only'}
				>
					{atLimit ? `You can choose up to ${max} people. Remove someone to add another.` : ''}
				</p>
			</div>
			{shownDepartments.length > 0 && (
				<div role="group" aria-label="Departments">
					<div aria-hidden className={SECTION_TITLE}>
						Departments
					</div>
					{shownDepartments.map((department, index) => (
						<PeopleSheetRow
							key={department._id}
							divider={index > 0}
							checked={department._id === selectedDepartment}
							onToggle={() => onSelectDepartment?.(department)}
							leading={
								<span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted">
									<Users className="size-4 text-muted-foreground" />
								</span>
							}
							name={department.name || 'Unnamed department'}
						/>
					))}
				</div>
			)}
			{people.length > 0 && (
				<div role="group" aria-label="People">
					{shownDepartments.length > 0 && (
						<div aria-hidden className={SECTION_TITLE}>
							People
						</div>
					)}
					{people.map((user, index) => {
						const label = getUserLabel(user) || 'Teammate';
						const name = getUserDisplayName(user);
						const picked = isPicked(user);
						return (
							<PeopleSheetRow
								key={user._id}
								divider={index > 0}
								checked={picked}
								disabled={atLimit && !picked}
								onToggle={() => onSelectUser(user)}
								leading={
									<Avatar className="size-8">
										<AvatarFallback
											className="text-xs font-medium text-white"
											// The same colour as the assignee's avatar on reminders
											style={{ backgroundColor: getAvatarColor(label) }}
										>
											{getInitials(user.name || user.username)}
										</AvatarFallback>
									</Avatar>
								}
								name={name}
								// People who share a name are told apart by email, as on desktop
								detail={label !== name ? emailOf(user) || user.username : undefined}
							/>
						);
					})}
				</div>
			)}
			{people.length === 0 && shownDepartments.length === 0 && (
				<p className="px-3 py-6 text-center text-[15px] text-muted-foreground">
					{query ? 'No matches' : 'No teammates to choose from'}
				</p>
			)}
		</BottomSheet>
	);
};

const PeopleSheetRow = ({
	checked,
	disabled,
	divider,
	leading,
	name,
	detail,
	onToggle,
}: {
	checked: boolean;
	disabled?: boolean;
	divider: boolean;
	leading: React.ReactNode;
	name: string;
	detail?: string;
	onToggle: () => void;
}) => (
	<button
		type="button"
		role="checkbox"
		aria-checked={checked}
		disabled={disabled}
		onClick={onToggle}
		className="relative -mx-4 flex min-h-12 w-[calc(100%+2rem)] items-center gap-3 px-4 py-1.5 text-left active:bg-muted disabled:opacity-40 dark:active:bg-zinc-800"
	>
		{divider && (
			<span aria-hidden className="absolute inset-x-0 top-0 ml-[3.75rem] h-px bg-border/70" />
		)}
		<span aria-hidden className="shrink-0">
			{leading}
		</span>
		<span className="min-w-0 flex-1">
			<span className="block truncate text-[17px] leading-snug">{name}</span>
			{detail && (
				<span className="block truncate text-[13px] leading-snug text-muted-foreground">
					{detail}
				</span>
			)}
		</span>
		<span
			aria-hidden
			className={cn(
				'flex size-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] transition-colors',
				checked ? 'border-primary bg-primary' : 'border-muted-foreground/40',
			)}
		>
			{checked && (
				<Check className="size-3.5 text-primary-foreground" strokeWidth={3} />
			)}
		</span>
	</button>
);
